import type {UseQueryOptions} from '@tanstack/react-query'
import Big from 'big.js'
import {groupBy} from 'remeda'

import {useIsAuthenticatedValue} from '@/auth/hooks/useIsAuthenticated'
import type {SupportedLocales} from '@/lib/locale/locale'
import type {Cart, CartWithGroupedItems} from '@/models/cartModel'
import {getCart} from '@/services/cartService'

export function getCartQueryKey(params: {locale: SupportedLocales}) {
  return ['cart', params.locale] as const
}

export function getCartQueryOptions<TData = Cart, TError = Error>(
  params: {locale: SupportedLocales; isAuthenticated: boolean},
  options?: Omit<UseQueryOptions<Cart, TError, TData>, 'queryKey' | 'queryFn'>,
) {
  return queryOptions({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- use the same query key to preserve old behavior
    queryKey: getCartQueryKey({
      locale: params.locale,
    }),
    queryFn: async ({signal}) =>
      getCart({
        isAuthenticated: params.isAuthenticated,
        locale: params.locale,
        signal,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 100000, // every 100s
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    ...options,
  })
}

export function useCart(
  locale: SupportedLocales,
  enabled = true,
  options?: Omit<UseQueryOptions<Cart, Error, CartWithGroupedItems>, 'queryKey'>,
) {
  const isAuthenticated = useIsAuthenticatedValue()

  return useQuery<Cart, Error, CartWithGroupedItems>({
    ...getCartQueryOptions({locale, isAuthenticated}),
    select: (data: Cart): CartWithGroupedItems => {
      // Share the same schema of useCustomCartValue
      return {
        ...data,
        itemsLength: data.items.reduce((acc, item) => acc + item.quantity, 0),
        itemsValue: data.items.reduce(
          (acc, item) => acc.plus(Big(item.price).mul(item.quantity)),
          Big(0),
        ),
        items: groupBy(data.items, item => item.seller ?? item.retailer),
        originalItems: data.items,
      }
    },
    enabled,
    ...options,
  })
}
